import React from "react";
import "./Skills"
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import {
  SiJavascript,
  SiReact,
  SiNodedotjs,
  SiTailwindcss,
  SiGit,
  SiLaravel,
  SiMongodb,
  SiMysql,
  SiPython,
  SiBootstrap,
  SiExpress,
} from "react-icons/si";
import { FaServer } from "react-icons/fa";

/* ================= SKILLS ================= */
const skills = [
  // outer
  { icon: SiJavascript, name: "JavaScript", x: -140, y: -80, color: "#F7DF1E" },
  { icon: SiReact, name: "React", x: 0, y: -130, color: "#61DAFB" },
  { icon: SiNodedotjs, name: "Node.js", x: 140, y: -80, color: "#339933" },
  { icon: SiExpress, name: "Express", x: -190, y: 40, color: "#000000" },
  { icon: FaServer, name: "REST API", x: 190, y: 40, color: "#4F46E5" },
  { icon: SiMongodb, name: "MongoDB", x: -120, y: 170, color: "#47A248" },
  { icon: SiMysql, name: "MySQL", x: 0, y: 210, color: "#4479A1" },
  { icon: SiTailwindcss, name: "Tailwind", x: 120, y: 170, color: "#38BDF8" },

  // inner (no overlap)
  { icon: SiPython, name: "Python", x: -60, y: -40, color: "#3776AB" },
  { icon: SiBootstrap, name: "Bootstrap", x: 60, y: -40, color: "#7952B3" },
  { icon: SiLaravel, name: "Laravel", x: -40, y: 60, color: "#FF2D20" },
  { icon: SiGit, name: "Git", x: 40, y: 60, color: "#F05032" },
];

/* ================= NODE ================= */
const SkillNode = ({ skill }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);

  const x = useSpring(
    useTransform(mx, [-200, 200], [skill.x - 16, skill.x + 16]),
    { stiffness: 30, damping: 18 }
  );

  const y = useSpring(
    useTransform(my, [-200, 200], [skill.y - 16, skill.y + 16]),
    { stiffness: 30, damping: 18 }
  );

  return (
    <motion.div
      style={{ x, y }}
      className="absolute left-1/2 top-1/2"
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set(e.clientX - r.left - r.width / 2);
        my.set(e.clientY - r.top - r.height / 2);
      }}
      onMouseLeave={() => {
        mx.set(0);
        my.set(0);
      }}
    >
      <motion.div
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        whileHover={{ scale: 1.18 }}
        className="group flex flex-col items-center"
      >
        <div
          className="
    rounded-xl
    bg-white dark:bg-neutral-900
    border border-neutral-200/70 dark:border-neutral-700/70
    shadow-md
    flex items-center justify-center
    transition
    group-hover:shadow-lg

    /* box size responsive */
    w-12 h-12
    sm:w-14 sm:h-14
    md:w-16 md:h-16
    lg:w-20 lg:h-20
  "
        >
          <skill.icon
            className="
      text-3xl   /* icon stays bold */
      md:text-3xl
      lg:text-4xl
    "
            style={{ color: skill.color }}
          />
        </div>

        <span
          className="
          hidden
            mt-2 text-xs sm:text-sm font-semibold
            text-neutral-600 dark:text-neutral-300
            opacity-0 group-hover:opacity-100
            transition
          "
        >
          {skill.name}
        </span>
      </motion.div>
    </motion.div>
  );
};

/* ================= PAGE ================= */
const Skills = () => {
  return (
    <section id="skills" className="relative min-h-screen flex items-center justify-center px-4 overflow-hidden"> 
      {/* background */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-neutral-50 via-white to-white dark:from-neutral-900 dark:to-black" />

      {/* title */}
      <div className="absolute top-16 sm:top-20 text-center">
        <h2 className="gradient-text text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-neutral-900 dark:text-neutral-100">
          Skills
        </h2>
        <p className="mt-3 text-sm sm:text-base text-neutral-500 dark:text-neutral-400">
          Tools and technologies I work with
        </p>
      </div>

      {/* Constellation Wrapper */}
      <div className="relative flex items-center justify-center w-full overflow-visible">
        <div
          className="
      relative
      w-[520px] h-[640px]
      origin-center
      scale-[0.65]
      sm:scale-[0.75]
      md:scale-[0.9]
      lg:scale-100
    "
        >
          {skills.map((skill, i) => (
            <SkillNode key={i} skill={skill} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Skills;
